import React, { useEffect, useState } from "react";
import useAuth from "../../hooks/auth_hook";
import usePayment from "../../hooks/payment_hook";
import { useNavigate } from "react-router-dom";
import PricingOption from "../../components/checkout/PricingOption";
import { Spinner } from "../../components/ui/spinner";
import { ToggleGroup, ToggleGroupItem } from "../../components/ui/toggle_group";

export default function Checkout() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { hasPaid, isCheckingPayment, isPaymentLoading, getCheckoutUrl, checkout_url } =
    usePayment();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "yearly">("yearly");
  const navigate = useNavigate();

  const redirectToCheckout = (productId: string, billingPeriod: "monthly" | "yearly") => {
    setIsRedirecting(true);
    const checkoutParams: Record<string, Record<"monthly" | "yearly", string>> = {
      under_10k_subs: {
        monthly: "under_10k_subs_monthly",
        yearly: "under_10k_subs_yearly",
      },
      the_everything_plan: {
        monthly: "the_everything_plan_monthly",
        yearly: "the_everything_plan_yearly",
      },
    };

    if (!checkoutParams[productId]?.[billingPeriod]) {
      console.error(`No checkout URL for product: ${productId}, period: ${billingPeriod}`);
      alert("Configuration error: Please contact support.");
      setIsRedirecting(false);
      return;
    }

    getCheckoutUrl(checkoutParams[productId][billingPeriod]);
  };

  useEffect(() => {
    if (isAuthenticated && user && !isLoading && !isCheckingPayment && hasPaid) {
      navigate("/add");
    }
  }, [isAuthenticated, isLoading, user, hasPaid, isCheckingPayment, navigate]);

  useEffect(() => {
    if (checkout_url && isRedirecting) {
      window.location.href = checkout_url;
    }
  }, [checkout_url, isRedirecting]);

  if (isLoading || isCheckingPayment) {
    return (
      <div className="flex justify-center items-center h-screen w-full">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <section className="pt-28 pb-20 w-full">
      <div className="max-w-5xl mx-auto px-4">
        <div className="text-center mb-16">
          <span className="text-lg font-semibold text-primary mb-2 block">
            Best Service, Best Price.
          </span>
          <h2 className="text-foreground font-bold text-3xl sm:text-4xl mb-4">Our Pricing Plan</h2>
          <p className="text-muted-foreground text-base max-w-[570px] mx-auto leading-relaxed mb-6">
            Both plans have the same features. 50% discount for new creators with less than 10k
            subscribers. You can keep the discount for life.
          </p>
          <div className="flex justify-center">
            <ToggleGroup
              type="single"
              value={billingPeriod}
              onValueChange={(val) => {
                if (val) setBillingPeriod(val as "monthly" | "yearly");
              }}
              variant="outline"
            >
              <ToggleGroupItem value="monthly" aria-label="Monthly billing">
                Monthly
              </ToggleGroupItem>
              <ToggleGroupItem value="yearly" aria-label="Yearly billing">
                Yearly
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-6">
          <PricingOption
            title="Less than 10k Subs"
            price={billingPeriod === "yearly" ? "$18" : "$24"}
            features={[
              "Unlimited Thumbnail A/B Tests",
              "Unlimited Title A/B Tests",
              "Unlimited Thumbnails with AI Generated Text",
              "Unlimited AI Generated Titles",
              "Hourly, Daily, Weekly, and Custom Length Tests",
              "1 Channel",
            ]}
            onPurchase={redirectToCheckout}
            billingPeriod={billingPeriod}
            productId="under_10k_subs"
            isPaymentLoading={isPaymentLoading}
          />
          <PricingOption
            title="The Everything Plan"
            price={billingPeriod === "yearly" ? "$37" : "$49"}
            features={[
              "Unlimited Thumbnail A/B Tests",
              "Unlimited Title A/B Tests",
              "Unlimited Thumbnails with AI Generated Text",
              "Unlimited AI Generated Titles",
              "Hourly, Daily, Weekly, and Custom Length Tests",
              "1 Channel",
            ]}
            onPurchase={redirectToCheckout}
            billingPeriod={billingPeriod}
            productId="the_everything_plan"
            isPaymentLoading={isPaymentLoading}
          />
        </div>

        <div className="mt-8 text-center text-sm text-muted-foreground">
          <p>
            P.S. When we say unlimited, we mean it.
            <br />
            Want to test 1000 thumbnails and 1000 titles? Go for it!
          </p>
        </div>
      </div>
    </section>
  );
}
